import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { ReactComponent as CloseIcon } from '@/assets/icons/cancel.icon.svg';
import { ReactComponent as WarnIcon } from '@/assets/icons/warn.icon.svg';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Oval } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import Input from '@/components/Input';
import { useFieldArray, useForm } from 'react-hook-form';
import { fetchSurvey, updateSurvey } from '@/api/surveyApi';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';

const schema = yup.object({
  title: yup.string().required('Title is required'),
  surveyType: yup.string().required('Survey type is required'),
  ratingQuestion: yup.array().of(
    yup.object().shape({
      question: yup.string().required('Question is required'),
      maxRating: yup
        .number()
        .required('Max rating is required')
        .min(1, 'Minimum is 1')
        .max(10, 'Maximum is 10')
    })
  ),
  // multiChoiceQuestion: yup.array().of(
  //   yup.object().shape({
  //     question: yup.string().required('Question is required'),
  //     options: yup.array().of(yup.string().required('Option is required'))
  //   })
  // )
});

function UpdateSurveyModal({ showModal, setShowModal, selectedItem }) {
  const queryClient = useQueryClient();

  const survey = useQuery({
    queryKey: ['survey', selectedItem?.id],
    queryFn: fetchSurvey,
    enabled: !!selectedItem?.id && showModal
  });

  const surveyData = survey?.data?.data;

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (surveyData) {
      reset({
        title: surveyData.title,
        surveyType: surveyData.surveyType,
        ratingQuestion: surveyData.ratingQuestion || [{ question: '', maxRating: 1 }],
        // multiChoiceQuestion: surveyData.multiChoiceQuestion || [{ question: '', options: ['', ''] }]
      });
    }
  }, [surveyData, reset]);

  const surveyType = watch('surveyType');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ratingQuestion'
  });

  // const { fields, append, remove } = useFieldArray({
  //   control,
  //   name: surveyType === 'rating' ? 'ratingQuestion' : 'multiChoiceQuestion'
  // });

  const handleUpdateSurvey = useMutation({
    mutationFn: ({id, data}) => updateSurvey(id, data),
    onSuccess: (responseData) => {
      reset();
      setShowModal(false);
      queryClient.setQueryData(['survey', selectedItem?.id], responseData);
      queryClient.invalidateQueries(['surveys']);
      toast.success('Survey Updated Successfully');
    },
    onError: (error) => {
      reset();
      setShowModal(false);
      toast.error(error?.response?.data?.message);
    }
  });

  const onSubmit = (data) => {

    // const submitData = {
    //   title: data.title,
    //   surveyType: data.surveyType,
    //   ...(data.surveyType === 'rating'
    //     ? { ratingQuestion: data.ratingQuestion }
    //     : { multiChoiceQuestion: data.multiChoiceQuestion })
    // };

    // console.log(submitData);
    handleUpdateSurvey.mutate({ id: selectedItem?.id , data });
  };

  return (
    <AnimatePresence>
      {showModal && (
        <ModalView
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="modal-overlay">
          <motion.div
            initial={{ y: 1000 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ duration: 0.3 }}
            className="modal"
            onClick={(e) => e.stopPropagation()}>
            {/* ======== Content ======== */}
            {survey.isFetching ? (
              <LoaderView>
                <div className="loading-view">
                  <Oval color="#92C22C" width={50} height={50} strokeWidth={5} />
                </div>
              </LoaderView>
            ) : (
              <ModalContent>
                <div className="header">
                  <button
                    type="button"
                    onClick={() => {
                      reset();
                      setShowModal(false);
                    }}>
                    <CloseIcon />
                  </button>
                </div>
                <ContentView>
                  <div className="content">
                    <div className="align">
                      <h3>Update Survey</h3>
                    </div>

                    <FormView onSubmit={handleSubmit(onSubmit)}>
                      <DataView>
                        <InputView>
                          <LabelView>
                            <label>Survey Title</label>
                          </LabelView>
                          <Input
                            type="text"
                            className="input"
                            placeholder="Enter a title"
                            {...register('title')}
                            error={!!errors?.title?.message}
                          />
                          {errors.title && (
                            <span className="error-label">{errors.title.message}</span>
                          )}
                        </InputView>
                        <InputView>
                          <LabelView>
                            <label>Survey Type</label>
                          </LabelView>
                          <select {...register('surveyType')}>
                            <option value="rating">Rating</option>
                            <option value="multichoice">Multiple Choice</option>
                          </select>
                          {errors.surveyType && (
                            <span className="error-label">{errors.surveyType.message}</span>
                          )}
                        </InputView>

                        {fields.map((item, index) => {
                          return (
                            <div key={item.id}>
                              <InputView>
                                <LabelView>
                                  <label>Question</label>
                                </LabelView>
                                <Input
                                  type="text"
                                  className="input"
                                  placeholder="Enter a question"
                                  {...register(`ratingQuestion.${index}.question`)}
                                  error={!!errors?.ratingQuestion?.[index]?.question?.message}
                                />
                                {errors.ratingQuestion?.[index]?.question && (
                                  <span className="error-label">
                                    {errors.ratingQuestion[index].question.message}
                                  </span>
                                )}
                              </InputView>
                              <div style={{ margin: '1rem' }} />
                              <InputView>
                                <LabelView>
                                  <label>Max Rating</label>
                                </LabelView>
                                <Input
                                  type="number"
                                  className="input"
                                  placeholder="Enter max rating"
                                  {...register(`ratingQuestion.${index}.maxRating`)}
                                  error={!!errors?.ratingQuestion?.[index]?.maxRating?.message}
                                />
                                {errors.ratingQuestion?.[index]?.maxRating && (
                                  <span className="error-label">
                                    {errors.ratingQuestion[index].maxRating.message}
                                  </span>
                                )}
                              </InputView>

                              {fields.length > 1 && (
                                <button
                                  className="delete"
                                  type="button"
                                  onClick={() => remove(index)}>
                                  Remove
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </DataView>

                      <div className="end">
                        <button
                          type="button"
                          onClick={() =>
                            append({ question: '', maxRating: 1 })
                          }>
                          Add Another Question
                        </button>
                      </div>

                      <div className="btn">
                        <button
                          type="button"
                          className="continue"
                          onClick={() => {
                            reset();
                            setShowModal(false);
                          }}>
                          No, Cancel
                        </button>
                        <button type="submit" className="activate">
                          {handleUpdateSurvey.isLoading ? (
                            <Oval height={20} width={20} color="#c2bdbd" secondaryColor="#ddd" />
                          ) : (
                            'Update Survey'
                          )}
                        </button>
                      </div>
                    </FormView>
                  </div>
                </ContentView>
              </ModalContent>
            )}
          </motion.div>
        </ModalView>
      )}
    </AnimatePresence>
  );
}

export default UpdateSurveyModal;

const ModalView = styled(motion.div)`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9009;
  display: flex;
  align-items: center;
  justify-content: center;

  .modal {
    background-color: ${(props) => props.theme.colors?.white};
    z-index: 30000;
    position: fixed;
    border-radius: 4px;
    width: 40vw;
    padding: 30px 0px;
    padding-top: 0;
    padding-bottom: 20px;
    border-radius: 10px;
    max-height: 90%;
    overflow-y: scroll;
  }
`;

const ModalContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  margin: 0;
  justify-content: flex-start;
  flex-direction: column;
  padding: 30px;

  .header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    h3 {
      font-size: 1rem;
      font-weight: 600;
    }
  }
`;

const LoaderView = styled.div`
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .loading-view {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ContentView = styled.div`
  width: 100%;

  .content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;

    .error {
      margin: 0.45rem 0;
      margin-left: -3rem;
      text-align: start;
      color: ${(props) => props.theme.colors?.error};

      .strong {
        font-weight: 500;
      }
    }

    .icon {
      /* path {
        stroke: ${(props) => props.theme.colors?.error_200};
      } */
    }

    .green-icon {
      path {
        stroke: ${(props) => props.theme.colors?.warning};
      }
    }

    .align {
      width: 100%;

      h3 {
        font-size: 1.2rem;
        font-weight: 600;
        margin-bottom: 10px;
        color: ${(props) => props.theme.colors?.secondary};
        margin-top: 10px;
        text-align: start;
      }

      p {
        padding-top: 1rem;
        margin-bottom: 0;
        font-size: 0.9rem;
        font-weight: 400;
        color: ${(props) => props.theme.colors?.darkGray};
        margin-bottom: 20px;
        line-height: 19px;
      }
    }

    .warn {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem;
      margin: 10px 0;
      background-color: #fdebeb;
      font-weight: 500;
      border-radius: 10px;

      h2 {
        width: 100%;
        font-weight: 500;
        line-height: 19px;
        color: ${(props) => props.theme.colors?.error_200};
      }
    }

    .currency-input {
      width: 100%;
      height: 64px;
      outline: none;
      margin-top: 5px;
      font-size: 1rem;
      font-weight: 300;
      text-indent: 15px;
      border-radius: 10px;
      transition: all 0.2s ease-in-out;
      background-color: ${({ theme }) => theme.colors?.inputBackground};
      border: 1px solid ${({ theme }) => theme.colors?.inputBackground};

      &:focus {
        background-color: ${({ theme }) => theme.colors?.white};
        border: 1px solid ${({ theme }) => theme.colors?.activeTitle};
      }

      &:disabled {
        background-color: #f2f2f2;
        color: #999999;
      }

      &::placeholder {
        color: ${({ theme }) => theme.colors?.placeholder};
        font-size: 1rem;
        font-weight: 300;
      }
    }

    .btn {
      width: 100%;
      gap: 1rem;
      display: flex;
      align-items: center;

      .activate {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 30px;
        background-color: ${(props) => props.theme.colors?.primary};
        height: 64px;
        border-radius: 16px;
        color: ${(props) => props.theme.colors?.white};
        font-weight: 500;
      }

      .continue {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 30px;
        background-color: ${(props) => props.theme.colors?.white};
        border: 1px solid ${(props) => props.theme.colors?.info};
        height: 64px;
        border-radius: 16px;
        color: ${(props) => props.theme.colors?.info};
        font-weight: 500;
      }
    }
  }
`;

const DataView = styled.div`
  width: 100%;
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: ${({ theme }) => theme.colors?.white};

  .disabled {
    background-color: #f2f2f2;
    color: #999999;
  }

  .check {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    margin-bottom: 0;
    gap: 1rem;

    .radio-input {
    }
  }

  .delete {
    margin: 0.4rem 0;
    color: ${({ theme }) => theme.colors.error_200};
  }
`;

const FormView = styled.form`
  width: 500px;

  .btn {
    margin-top: 40px;

    .approve {
      width: 40%;
      border-radius: 18px;
      color: ${({ theme }) => theme.colors.white};
      background-color: ${({ theme }) => theme.colors.primary};
    }
  }

  .end {
    display: flex;
    align-items: center;
    justify-content: end;
    margin: 1rem 0;

    button {
      color: ${({ theme }) => theme.colors?.placeholder};
    }
  }
`;

const InputView = styled.div`
  width: 100%;
  position: relative;

  .input::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }

  select {
    padding: 0 1rem;
    margin-top: 5px;
    width: 100%;
    height: 4rem;
    border-radius: 5px;
    border: none;
    outline: none;
    background-color: ${({ theme }) => theme.colors?.inputBackground};
  }

  .icon {
    background-color: transparent;
    padding: 0;
    margin: 0;
    outline: none;
    border: none;
    position: absolute;
    top: 2.85rem;
    right: 12px;
    cursor: pointer;

    svg {
      transition: all 0.3s ease-in-out;
      transform: ${({ open }) => (open ? 'rotate(180deg)' : 'none')};
      transform-origin: 50% 50%;
      width: 20px;
      height: 15px;
    }
  }

  .input {
    position: relative;

    button {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
      margin-top: 5px;
    }
  }

  .error-label {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.error_200};
    margin-top: 5px;
  }

  p {
    margin-top: 5px;
    text-align: end;
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.placeholder};
  }
`;

const LabelView = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: 0px;

  label {
    font-size: 1rem;
  }
`;
